import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DownloadSimple, 
  Database, 
  Clock, 
  CheckCircle,
  Warning,
  Spinner,
  FileZip,
  ArrowClockwise,
  CalendarBlank,
  Timer,
  Gear,
  Play,
  Stop
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BackupRestore = ({ adminId }) => {
  const [loading, setLoading] = useState(false);
  const [backupInfo, setBackupInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  
  // Scheduled backup states
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState('daily'); // daily or weekly
  const [scheduleTime, setScheduleTime] = useState('02:00');
  const [scheduleDay, setScheduleDay] = useState('monday'); // for weekly
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [lastAutoBackup, setLastAutoBackup] = useState(null);

  // Load backup info on mount
  useEffect(() => {
    loadBackupInfo();
    loadScheduleSettings();
  }, []);

  const loadBackupInfo = async () => {
    try {
      setLoadingInfo(true);
      const response = await axios.get(`${API}/admin/backup/info`);
      setBackupInfo(response.data);
    } catch (error) {
      console.error('Failed to load backup info:', error);
      // No backup exists yet - that's okay
      setBackupInfo(null);
    } finally {
      setLoadingInfo(false);
    }
  };

  const loadScheduleSettings = async () => {
    try {
      setScheduleLoading(true);
      const response = await axios.get(`${API}/admin/backup/schedule`);
      if (response.data) {
        setScheduleEnabled(response.data.enabled || false);
        setScheduleFrequency(response.data.frequency || 'daily');
        setScheduleTime(response.data.time || '02:00');
        setScheduleDay(response.data.day || 'monday');
        setLastAutoBackup(response.data.last_auto_backup || null);
      }
    } catch (error) {
      console.error('Failed to load schedule settings:', error);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    try {
      setSavingSchedule(true);
      const response = await axios.post(`${API}/admin/backup/schedule`, {
        enabled: scheduleEnabled,
        frequency: scheduleFrequency,
        time: scheduleTime,
        day: scheduleDay,
        admin_id: adminId
      });
      
      if (response.data.success) {
        toast.success(scheduleEnabled ? 'Automatic backup schedule सेव हो गया!' : 'Automatic backup बंद हो गया');
      }
    } catch (error) {
      console.error('Failed to save schedule:', error);
      toast.error('Schedule save करने में error हुआ');
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleCreateBackup = async () => {
    try {
      setLoading(true);
      toast.info('Backup बन रहा है... कृपया प्रतीक्षा करें');

      // Create backup
      const response = await axios.post(`${API}/admin/backup/create`, {
        admin_id: adminId
      });

      if (response.data.success) {
        toast.success('Backup सफलतापूर्वक बन गया!');
        
        // Trigger download
        const userStr = localStorage.getItem('dws_user');
        const token = userStr ? JSON.parse(userStr).token : '';
        window.location.href = `${API}/admin/backup/download?token=${token}`;
        
        // Reload backup info
        await loadBackupInfo();
      }
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error(error.response?.data?.detail || 'Backup बनाने में error हुआ');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = () => {
    if (!backupInfo?.exists) {
      toast.error('कोई backup उपलब्ध नहीं है। पहले backup बनाएं।');
      return;
    }
    const userStr = localStorage.getItem('dws_user');
    const token = userStr ? JSON.parse(userStr).token : '';
    window.location.href = `${API}/admin/backup/download?token=${token}`;
    toast.success('Download शुरू हो गया!');
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('hi-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="bg-wa-panel rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-wa-text-primary flex items-center gap-2">
            <Database size={24} weight="duotone" className="text-indigo-600" />
            Backup & Restore
          </h2>
          <p className="text-sm text-wa-text-secondary mt-1">
            पूरी Application का backup लें - Code + Database + Settings
          </p>
        </div>
      </div>

      <div className="border-t pt-4">
        {/* Backup Info Card */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <FileZip size={40} weight="duotone" className="text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-wa-text-primary mb-1">Full Application Backup</h3>
              <p className="text-sm text-wa-text-primary mb-3">
                इस backup में शामिल है:
              </p>
              <ul className="text-sm text-indigo-600 space-y-1 mb-3">
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Database Data (Users, Chats, Messages, Groups)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Complete Source Code (Frontend + Backend)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  App Settings & Configuration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle size={16} weight="fill" className="text-green-600" />
                  Recreation Instructions (README)
                </li>
              </ul>
              
              {/* Last Backup Info */}
              {loadingInfo ? (
                <div className="flex items-center gap-2 text-sm text-wa-text-secondary">
                  <Spinner size={16} className="animate-spin" />
                  Loading...
                </div>
              ) : backupInfo?.exists ? (
                <div className="bg-wa-panel rounded-lg p-3 border border-indigo-200">
                  <div className="flex items-center gap-2 text-sm text-wa-text-primary mb-1">
                    <CheckCircle size={16} weight="fill" />
                    <span className="font-medium">Last Backup Available</span>
                  </div>
                  <div className="text-xs text-wa-text-secondary space-y-1">
                    <p className="flex items-center gap-2">
                      <Clock size={14} />
                      {formatDateTime(backupInfo.created_at)}
                    </p>
                    <p>Size: {formatFileSize(backupInfo.size)}</p>
                    <p>Filename: {backupInfo.filename}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-wa-hover rounded-lg p-3 border border-yellow-200">
                  <div className="flex items-center gap-2 text-sm text-wa-text-primary">
                    <Warning size={16} weight="fill" />
                    <span>कोई backup नहीं बना है अभी तक</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="bg-wa-hover border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <Warning size={20} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-wa-text-primary">
              <p className="font-semibold mb-1">Important:</p>
              <ul className="list-disc list-inside space-y-1 text-wa-text-primary">
                <li>नया backup बनाने पर पुराना backup replace हो जाएगा</li>
                <li>Backup में current state save होता है (deleted items नहीं आएंगे)</li>
                <li>Backup file phone में download करके safe रखें</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleCreateBackup}
            disabled={loading}
            className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <Spinner size={20} className="animate-spin" />
                Backup बन रहा है...
              </>
            ) : (
              <>
                <DownloadSimple size={20} weight="bold" />
                Create & Download Backup
              </>
            )}
          </button>

          {backupInfo?.exists && (
            <button
              onClick={handleDownloadBackup}
              disabled={loading}
              className="flex-1 bg-wa-primary-strong hover:bg-green-700 disabled:opacity-50 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <ArrowClockwise size={20} weight="bold" />
              Download Last Backup
            </button>
          )}
        </div>

        {/* Usage Guide */}
        <div className="mt-4 p-3 bg-wa-hover rounded-lg border border-wa-border">
          <h4 className="text-sm font-semibold text-wa-text-primary mb-2">Backup का उपयोग कैसे करें:</h4>
          <ol className="text-xs text-wa-text-secondary space-y-1 list-decimal list-inside">
            <li>Backup download करें और safe रखें (Phone/Computer/Drive)</li>
            <li>Emergency में ZIP file को नए Agent को दें</li>
            <li>Agent README.md पढ़कर पूरी app recreate कर देगा</li>
            <li>Same data, same features, same look - सब वापस!</li>
          </ol>
        </div>

        {/* Automatic Scheduled Backup Section */}
        <div className="mt-6 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Timer size={24} weight="duotone" className="text-purple-600" />
              <div>
                <h3 className="font-semibold text-wa-text-primary">Automatic Scheduled Backup</h3>
                <p className="text-xs text-wa-text-secondary">बिना याद किए, automatic backup होता रहेगा</p>
              </div>
            </div>
            
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => setScheduleEnabled(!scheduleEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                scheduleEnabled ? 'bg-purple-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-wa-panel transition-transform ${
                  scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {scheduleLoading ? (
            <div className="flex items-center gap-2 text-sm text-wa-text-secondary py-4">
              <Spinner size={16} className="animate-spin" />
              Loading schedule settings...
            </div>
          ) : (
            <div className={`bg-wa-hover border border-purple-200 rounded-lg p-4 ${!scheduleEnabled ? 'opacity-60' : ''}`}>
              {/* Schedule Status */}
              <div className="flex items-center gap-2 mb-4">
                {scheduleEnabled ? (
                  <div className="flex items-center gap-2 text-wa-text-primary text-sm">
                    <Play size={16} weight="fill" />
                    <span className="font-medium">Automatic Backup: चालू</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-wa-text-secondary text-sm">
                    <Stop size={16} weight="fill" />
                    <span className="font-medium">Automatic Backup: बंद</span>
                  </div>
                )}
              </div>

              {/* Frequency Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-wa-text-primary mb-2">
                    <CalendarBlank size={16} className="inline mr-1" />
                    Frequency (कितनी बार)
                  </label>
                  <select
                    value={scheduleFrequency}
                    onChange={(e) => setScheduleFrequency(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-wa-panel disabled:bg-wa-hover disabled:cursor-not-allowed"
                  >
                    <option value="daily">Daily (रोज़)</option>
                    <option value="weekly">Weekly (हफ्ते में एक बार)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-wa-text-primary mb-2">
                    <Clock size={16} className="inline mr-1" />
                    Time (कितने बजे)
                  </label>
                  <select
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-wa-panel disabled:bg-wa-hover disabled:cursor-not-allowed"
                  >
                    <option value="00:00">12:00 AM (रात 12 बजे)</option>
                    <option value="01:00">01:00 AM (रात 1 बजे)</option>
                    <option value="02:00">02:00 AM (रात 2 बजे)</option>
                    <option value="03:00">03:00 AM (रात 3 बजे)</option>
                    <option value="04:00">04:00 AM (सुबह 4 बजे)</option>
                    <option value="05:00">05:00 AM (सुबह 5 बजे)</option>
                    <option value="06:00">06:00 AM (सुबह 6 बजे)</option>
                    <option value="12:00">12:00 PM (दोपहर 12 बजे)</option>
                    <option value="18:00">06:00 PM (शाम 6 बजे)</option>
                    <option value="21:00">09:00 PM (रात 9 बजे)</option>
                    <option value="23:00">11:00 PM (रात 11 बजे)</option>
                  </select>
                </div>
              </div>

              {/* Day Selection for Weekly */}
              {scheduleFrequency === 'weekly' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-wa-text-primary mb-2">
                    Day (कौन सा दिन)
                  </label>
                  <select
                    value={scheduleDay}
                    onChange={(e) => setScheduleDay(e.target.value)}
                    disabled={!scheduleEnabled}
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:outline-none bg-wa-panel disabled:bg-wa-hover disabled:cursor-not-allowed"
                  >
                    <option value="sunday">Sunday (रविवार)</option>
                    <option value="monday">Monday (सोमवार)</option>
                    <option value="tuesday">Tuesday (मंगलवार)</option>
                    <option value="wednesday">Wednesday (बुधवार)</option>
                    <option value="thursday">Thursday (गुरुवार)</option>
                    <option value="friday">Friday (शुक्रवार)</option>
                    <option value="saturday">Saturday (शनिवार)</option>
                  </select>
                </div>
              )}

              {/* Save Button */}
              <button
                onClick={handleSaveSchedule}
                disabled={savingSchedule}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {savingSchedule ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Gear size={18} weight="bold" />
                    Save Schedule Settings
                  </>
                )}
              </button>

              {/* Schedule Summary */}
              {scheduleEnabled && (
                <div className="mt-3 p-2 bg-wa-panel rounded border border-purple-200 text-xs text-wa-text-primary">
                  <p className="font-medium">
                    {scheduleFrequency === 'daily' 
                      ? `📅 रोज़ ${scheduleTime} बजे automatic backup होगा`
                      : `📅 हर ${scheduleDay === 'sunday' ? 'रविवार' : scheduleDay === 'monday' ? 'सोमवार' : scheduleDay === 'tuesday' ? 'मंगलवार' : scheduleDay === 'wednesday' ? 'बुधवार' : scheduleDay === 'thursday' ? 'गुरुवार' : scheduleDay === 'friday' ? 'शुक्रवार' : 'शनिवार'} को ${scheduleTime} बजे backup होगा`
                    }
                  </p>
                  {lastAutoBackup && (
                    <p className="mt-1 text-wa-text-secondary">
                      Last auto backup: {formatDateTime(lastAutoBackup)}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackupRestore;
